import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import SectionMagazine5 from "./SectionMagazine5";
import SectionLatestPosts from "./SectionLatestPosts";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { fetchBlogs } from "redux/features/blog/blogSlice";
import "./BlogPage.css";

const BlogPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  let locationState = useAppSelector((state) => state.featureBlog.blogs);

  useEffect(() => {
    dispatch(fetchBlogs()).finally(() => {
      setTimeout(() => setIsLoading(false), 300);
    });
  }, [dispatch]);

  // Filter posts based on search and category
  const filteredPosts = locationState.filter((post: any) => {
    const matchesSearch =
      searchQuery === "" ||
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description?.toLowerCase().includes(searchQuery.toLowerCase());

    // Use tags as categories if category doesn't exist
    const postCategory = post.category || (post.tags && post.tags[0]) || "Non classé";
    const matchesCategory = selectedCategory === "all" || postCategory === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Extract categories from category field or first tag, with fallback
  const extractedCategories = locationState
    .map((post: any) => {
      return post.category || (post.tags && post.tags[0]) || "Non classé";
    })
    .filter(Boolean);

  const categories = ["all", ...Array.from(new Set(extractedCategories))];

  return (
    <div className="relative overflow-hidden nc-BlogPage min-h-screen">
      <Helmet>
        <title>Event To Bee - Blog Moderne</title>
        <meta
          name="description"
          content="Découvrez les derniers articles et tendances sur Event To Bee. Blog moderne avec design responsive et UX optimisée."
        />
        <link rel="canonical" href="https://eventtobee.com/blog" />
      </Helmet>

      {/* ======== BG GLASS ======== */}
      <BgGlassmorphism />

      {/* Hero Section with Search */}
      <div className="relative pt-16 pb-12">
        <div className="container relative">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-full text-sm font-medium text-primary-600 mb-6">
              <span className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse"></span>
              Blog Event To Bee
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent mb-6">
              Inspirations & Tendances
            </h1>
            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto mb-8">
              Découvrez les dernières tendances, conseils d'experts et inspirations pour créer des événements
              inoubliables
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl blur-sm opacity-30 group-hover:opacity-50 transition-opacity"></div>
              <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                <div className="flex items-center p-4">
                  <svg
                    className="w-5 h-5 text-neutral-400 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Rechercher des articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent text-lg placeholder-neutral-400 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg"
                      : "bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700"
                  }`}
                >
                  {category === "all" ? "Tous" : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="container relative">
        {isLoading ? (
          /* Loading Skeleton */
          <div className="space-y-12 animate-pulse">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
              <div className="h-96 bg-neutral-200 dark:bg-neutral-700 rounded-3xl"></div>
              <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-28 bg-neutral-200 dark:bg-neutral-700 rounded-2xl"></div>
                ))}
              </div>
            </div>
            <div className="grid gap-6 md:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-40 bg-neutral-200 dark:bg-neutral-700 rounded-2xl"></div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* === SECTION Magazine === */}
            <div className="pb-16 lg:pb-28 animate-fade-in-up animation-delay-200">
              <SectionMagazine5 posts={filteredPosts} />
            </div>

            {/* === SECTION Latest Posts === */}
            <div className="animate-fade-in-up animation-delay-400">
              <SectionLatestPosts posts={filteredPosts} className="py-16 lg:py-28" />
            </div>

            {/* No Results */}
            {filteredPosts.length === 0 && !isLoading && (
              <div className="text-center py-20 animate-fade-in">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.44-1.01-5.907-2.624l.077-.176m0 0A7.973 7.973 0 015.145 8H2m14.855 0H21M4 20l4.586-4.586M20 20l-4.586-4.586"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-neutral-800 dark:text-neutral-200 mb-3">
                  Aucun article trouvé
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                  Essayez d'ajuster vos filtres ou votre recherche
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("all");
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-full font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center"
        aria-label="Retour en haut"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default BlogPage;
